import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  Calendar,
  Briefcase,
  Shield,
  Clock,
  Save,
  Eye,
  TrendingUp,
  ShoppingCart,
  CreditCard,
  DollarSign,
  Users,
  Award,
  Activity,
  BarChart3,
  PieChart,
  Target,
} from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { getUserPhotoUrl, getUserInitials } from "@/utils/user";
import { format, startOfMonth, endOfMonth, subMonths, startOfDay, endOfDay, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useTenant } from "@/contexts/TenantContext";
import { logCrud } from "@/services/activities";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

type UserProfile = {
  id: string;
  nome_completo: string;
  email: string;
  telefone: string | null;
  cargo: string | null;
  status: string;
  data_nascimento: string | null;
  tenant_id: string | null;
  role_id: string | null;
  created_at: string;
  updated_at?: string | null;
  comissao?: number | null;
  foto_url?: string | null;
  chatwoot_avatar_url?: string | null;
  roles?: {
    name: string;
    slug: string;
  };
  tenants?: {
    name: string;
    slug: string;
  };
};

type Transaction = {
  id: string;
  tipo: string;
  valor_total: number;
  valor_liquido: number;
  desconto: number;
  status: string;
  created_at: string;
  descricao: string | null;
  client_id: string | null;
  payment_method_id?: string | null;
  payment_methods?: {
    nome: string;
  } | null;
  clients?: {
    nome: string;
  } | null;
  transaction_items?: {
    id: string;
    quantidade: number;
    preco_unitario: number;
    total: number;
    products: {
      nome: string;
    } | null;
  }[];
  transaction_payments?: Array<{
    id: string;
    transaction_id: string;
    valor: number;
    status: string;
    payment_methods?: {
      nome: string;
    } | null;
  }>;
};

type UserStats = {
  totalVendas: number;
  totalVendido: number;
  ticketMedio: number;
  totalComissao: number;
  ultimaVenda: string | null;
  vendasMes: number;
  vendasSemana: number;
  topProducts: { name: string; quantity: number; revenue: number }[];
  topClients: { name: string; quantidade: number; revenue: number }[];
  monthlySales: { month: string; value: number }[];
  weeklySales: { week: string; value: number }[];
  dailySales: { day: string; value: number }[];
  paymentMethods: { method: string; value: number }[];
  salesByStatus: { status: string; count: number }[];
  salesByDayOfWeek: { day: string; value: number }[];
  salesByHour: { hour: number; value: number }[];
};

export default function UserDetails(props?: { id?: string; embedded?: boolean; periodRange?: [Date, Date]; setLoading?: (loading: boolean) => void }) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = props?.id ?? routeId;
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    nome_completo: "",
    telefone: "",
    cargo: "",
    status: "ativo",
    data_nascimento: "",
    role_id: "",
  });
  const { tenantId, isAdminSaas } = useTenant();

  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ["user", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles" as any)
        .select(`
          *,
          roles:role_id (name, slug),
          tenants:tenant_id (name, slug)
        `)
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as unknown as UserProfile;
    },
    enabled: !!id,
  });

  // Buscar transações do usuário (vendas realizadas)
  const search = new URLSearchParams(location.search);
  const isEmbedded = (typeof props?.embedded !== 'undefined') ? !!props?.embedded : (search.get("embedded") === "1");
  const fromStr = search.get("from");
  const toStr = search.get("to");
  const cadFrom = props?.periodRange ? startOfDay(props.periodRange[0]) : (fromStr ? startOfDay(new Date(fromStr)) : startOfDay(new Date(0)));
  const cadTo = props?.periodRange ? endOfDay(props.periodRange[1]) : (toStr ? endOfDay(new Date(toStr)) : endOfDay(new Date()));
  const { data: transactions, isLoading: transactionsLoading } = useQuery({
    queryKey: ["user-transactions", id, tenantId, cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      if (!id) return [];

      let query = supabase
        .from("transactions" as any)
        .select(`
          *,
          clients (nome),
          payment_methods:payment_method_id (nome),
          transaction_items (
            id,
            quantidade,
            preco_unitario,
            total,
            products (nome)
          )
        `)
        .eq("vendedor_id", id)
        .eq("tipo", "entrada")
        .gte("created_at", cadFrom.toISOString())
        .lte("created_at", cadTo.toISOString())
        .order("created_at", { ascending: false });

      if (tenantId && !isAdminSaas) {
        query = query.eq("tenant_id", tenantId);
      }

      const { data, error } = await query as any;

      if (error) throw error;
      const transactionsData = (data || []) as Transaction[];

      // Buscar pagamentos separadamente para cada transação
      if (transactionsData.length > 0) {
        try {
          const transactionIds = transactionsData.map((t) => t.id);
          const { data: paymentsData, error: paymentsError } = await supabase
            .from("transaction_payments" as any)
            .select(`
              id,
              transaction_id,
              valor,
              status,
              payment_methods:payment_method_id (nome)
            `)
            .in("transaction_id", transactionIds) as any;

          // Se não houver erro e houver dados, agrupar pagamentos por transação
          if (!paymentsError && paymentsData) {
            const paymentsByTransaction = new Map<string, any[]>();
            paymentsData.forEach((payment: any) => {
              const txId = payment.transaction_id;
              if (!paymentsByTransaction.has(txId)) {
                paymentsByTransaction.set(txId, []);
              }
              paymentsByTransaction.get(txId)!.push(payment);
            });

            // Adicionar pagamentos às transações
            transactionsData.forEach((transaction) => {
              transaction.transaction_payments = paymentsByTransaction.get(transaction.id) || [];
            });
          } else {
            // Se houver erro ou não houver dados, inicializar com array vazio
            transactionsData.forEach((transaction) => {
              transaction.transaction_payments = [];
            });
          }
        } catch (error) {
          // Se houver erro ao buscar pagamentos, inicializar com array vazio
          transactionsData.forEach((transaction) => {
            transaction.transaction_payments = [];
          });
        }
      }

      return transactionsData;
    },
    enabled: !!id,
  });

  useEffect(() => {
    const loading = userLoading || transactionsLoading;
    if (props?.setLoading) props.setLoading(loading);
  }, [userLoading, transactionsLoading, props?.setLoading]);

  // Buscar estatísticas do usuário
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["user-stats", id, transactions],
    queryFn: async () => {
      if (!transactions || transactions.length === 0) {
        return {
          totalVendas: 0,
          totalVendido: 0,
          ticketMedio: 0,
          totalComissao: 0,
          ultimaVenda: null,
          vendasMes: 0,
          vendasSemana: 0,
          topProducts: [],
          topClients: [],
          monthlySales: [],
          weeklySales: [],
          dailySales: [],
          paymentMethods: [],
          salesByStatus: [],
          salesByDayOfWeek: [],
          salesByHour: [],
        } as UserStats;
      }

      const vendas = transactions.filter((t) => t.status === "paga" || t.status === "confirmada" || t.status === "confirmado");
      const totalVendas = vendas.length;
      const totalVendido = vendas.reduce((sum, t) => sum + Number(t.valor_liquido || 0), 0);
      const ticketMedio = totalVendas > 0 ? totalVendido / totalVendas : 0;
      const totalComissao = user?.comissao
        ? (totalVendido * Number(user.comissao)) / 100
        : 0;
      const ultimaVenda = vendas.length > 0 ? vendas[0].created_at : null;

      // Vendas do mês
      const now = new Date();
      const startOfCurrentMonth = startOfMonth(now);
      const vendasMes = vendas.filter(
        (t) => new Date(t.created_at) >= startOfCurrentMonth
      ).length;

      // Vendas da semana
      const startOfCurrentWeek = new Date(now);
      startOfCurrentWeek.setDate(now.getDate() - now.getDay());
      const vendasSemana = vendas.filter(
        (t) => new Date(t.created_at) >= startOfCurrentWeek
      ).length;

      // Top produtos
      const productMap = new Map<string, { quantity: number; revenue: number }>();
      vendas.forEach((venda) => {
        venda.transaction_items?.forEach((item) => {
          const productName = item.products?.nome || "Produto Desconhecido";
          const current = productMap.get(productName) || { quantity: 0, revenue: 0 };
          productMap.set(productName, {
            quantity: current.quantity + item.quantidade,
            revenue: current.revenue + Number(item.total || 0),
          });
        });
      });
      const topProducts = Array.from(productMap.entries())
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

      // Top clientes
      const clientMap = new Map<string, { quantidade: number; revenue: number }>();
      vendas.forEach((venda) => {
        if (venda.client_id && venda.clients) {
          const clientName = venda.clients.nome;
          const current = clientMap.get(clientName) || { quantidade: 0, revenue: 0 };
          clientMap.set(clientName, {
            quantidade: current.quantidade + 1,
            revenue: current.revenue + Number(venda.valor_liquido || 0),
          });
        }
      });
      const topClients = Array.from(clientMap.entries())
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

      // Vendas mensais (últimos 6 meses)
      const monthlyMap = new Map<string, number>();
      vendas.forEach((venda) => {
        const month = format(new Date(venda.created_at), "MMM/yy", { locale: ptBR });
        monthlyMap.set(month, (monthlyMap.get(month) || 0) + Number(venda.valor_liquido || 0));
      });
      const monthlySales = Array.from(monthlyMap.entries())
        .map(([month, value]) => ({ month, value }))
        .slice(-6);

      // Vendas semanais (últimas 8 semanas)
      const weeklyMap = new Map<string, number>();
      vendas.forEach((venda) => {
        const week = format(new Date(venda.created_at), "dd/MM", { locale: ptBR });
        weeklyMap.set(week, (weeklyMap.get(week) || 0) + Number(venda.valor_liquido || 0));
      });
      const weeklySales = Array.from(weeklyMap.entries())
        .map(([week, value]) => ({ week, value }))
        .slice(-8);

      // Vendas diárias (últimos 30 dias)
      const dailyMap = new Map<string, number>();
      vendas.forEach((venda) => {
        const day = format(new Date(venda.created_at), "dd/MM", { locale: ptBR });
        dailyMap.set(day, (dailyMap.get(day) || 0) + Number(venda.valor_liquido || 0));
      });
      const dailySales = Array.from(dailyMap.entries())
        .map(([day, value]) => ({ day, value }))
        .slice(-30);

      // Métodos de pagamento
      const paymentMap = new Map<string, number>();
      vendas.forEach((venda) => {
        if (venda.transaction_payments && venda.transaction_payments.length > 0) {
          // Usar transaction_payments quando disponível
          venda.transaction_payments.forEach((payment) => {
            if (payment.status === "confirmado") {
              const methodName =
                (payment.payment_methods as any)?.nome || "Não especificado";
              paymentMap.set(
                methodName,
                (paymentMap.get(methodName) || 0) + Number(payment.valor || 0)
              );
            }
          });
        } else if (venda.payment_methods) {
          // Se não houver transaction_payments, usar payment_methods da transaction
          const methodName = venda.payment_methods.nome || "Não especificado";
          paymentMap.set(
            methodName,
            (paymentMap.get(methodName) || 0) + Number(venda.valor_liquido || 0)
          );
        } else {
          // Fallback: método não especificado
          const defaultMethod = "Não especificado";
          paymentMap.set(
            defaultMethod,
            (paymentMap.get(defaultMethod) || 0) + Number(venda.valor_liquido || 0)
          );
        }
      });
      const paymentMethods = Array.from(paymentMap.entries()).map(([method, value]) => ({
        method,
        value,
      }));

      // Vendas por status
      const statusMap = new Map<string, number>();
      transactions.forEach((t) => {
        statusMap.set(t.status, (statusMap.get(t.status) || 0) + 1);
      });
      const salesByStatus = Array.from(statusMap.entries()).map(([status, count]) => ({
        status,
        count,
      }));

      // Vendas por dia da semana
      const dayOfWeekMap = new Map<string, number>();
      vendas.forEach((venda) => {
        const dayName = format(new Date(venda.created_at), "EEEE", { locale: ptBR });
        dayOfWeekMap.set(
          dayName,
          (dayOfWeekMap.get(dayName) || 0) + Number(venda.valor_liquido || 0)
        );
      });
      const salesByDayOfWeek = Array.from(dayOfWeekMap.entries()).map(([day, value]) => ({
        day: day.charAt(0).toUpperCase() + day.slice(1),
        value,
      }));

      // Vendas por hora do dia
      const hourMap = new Map<number, number>();
      vendas.forEach((venda) => {
        const hour = new Date(venda.created_at).getHours();
        hourMap.set(hour, (hourMap.get(hour) || 0) + Number(venda.valor_liquido || 0));
      });
      const salesByHour = Array.from(hourMap.entries())
        .map(([hour, value]) => ({ hour, value }))
        .sort((a, b) => a.hour - b.hour);

      return {
        totalVendas,
        totalVendido,
        ticketMedio,
        totalComissao,
        ultimaVenda,
        vendasMes,
        vendasSemana,
        topProducts,
        topClients,
        monthlySales,
        weeklySales,
        dailySales,
        paymentMethods,
        salesByStatus,
        salesByDayOfWeek,
        salesByHour,
      } as UserStats;
    },
    enabled: !!transactions,
  });

  // Buscar roles disponíveis
  const { data: availableRoles = [] } = useQuery({
    queryKey: ["roles-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("roles" as any)
        .select("id, name, slug")
        .order("name", { ascending: true }) as any;

      if (error) throw error;
      return data.filter((r: any) => r.slug !== "admin_saas") as Array<{
        id: string;
        name: string;
        slug: string;
      }>;
    },
  });

  // Buscar permissões do role
  const { data: permissions = [] } = useQuery({
    queryKey: ["user-permissions", user?.role_id],
    queryFn: async () => {
      if (!user?.role_id) return [];

      const { data, error } = await supabase
        .from("role_permissions_junction" as any)
        .select(`
          permissions:permission_id (
            id,
            name,
            slug,
            module,
            action
          )
        `)
        .eq("role_id", user.role_id) as any;

      if (error) throw error;
      return (data || []).map((item: any) => item.permissions).filter(Boolean);
    },
    enabled: !!user?.role_id,
  });

  // Atualizar formData quando o usuário for carregado
  useEffect(() => {
    if (user) {
      setFormData({
        nome_completo: user.nome_completo,
        telefone: user.telefone || "",
        cargo: user.cargo || "",
        status: user.status || "ativo",
        data_nascimento: user.data_nascimento || "",
        role_id: user.role_id || "",
      });
    }
  }, [user]);

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { data: fnData, error: fnError } = await supabase.functions.invoke("admin-update-user", {
        body: {
          id: id,
          nome_completo: data.nome_completo,
          telefone: data.telefone || null,
          cargo: data.cargo || null,
          status: data.status,
          data_nascimento: data.data_nascimento || null,
          role_id: data.role_id || null,
          tenant_id: tenantId || null,
        },
      });

      if (fnError) throw fnError;
      if (!fnData?.success) throw new Error("Falha ao atualizar usuário");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", id] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Usuário atualizado com sucesso");
      if (id) {
        void logCrud({ entitySlug: "entity:profiles", operationSlug: "update", entityId: id, screenSlug: "usuarios" });
      }
      setIsEditing(false);
    },
    onError: () => toast.error("Erro ao atualizar usuário"),
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  // Máscara para telefone brasileiro
  const formatPhoneBR = (value: string) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 10) {
      return digits
        .replace(/^(\d{0,2})(\d)/, "($1) $2")
        .replace(/(\d{4})(\d)/, "$1-$2")
        .slice(0, 14);
    }
    return digits
      .replace(/^(\d{0,2})(\d)/, "($1) $2")
      .replace(/(\d{5})(\d)/, "$1-$2")
      .slice(0, 15);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<
      string,
      "default" | "secondary" | "destructive" | "success" | "outline"
    > = {
      ativo: "success",
      inativo: "secondary",
      suspenso: "destructive",
    };
    return variants[status] || "outline";
  };

  const COLORS = [
    "hsl(var(--primary))",
    "hsl(var(--secondary))",
    "hsl(var(--accent))",
    "hsl(var(--muted))",
    "hsl(var(--card))",
  ];

  // Timeline de atividades (últimas transações)
  const timelineActivities = useMemo(() => {
    if (!transactions) return [];
    return transactions.slice(0, 20).map((transaction) => ({
      id: transaction.id,
      tipo: "Venda realizada",
      descricao: `Venda de R$ ${Number(transaction.valor_liquido || 0).toFixed(2)} para ${
        transaction.clients?.nome || "Cliente não identificado"
      }`,
      created_at: transaction.created_at,
    }));
  }, [transactions]);

  if (userLoading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <div className="p-8">Usuário não encontrado</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {!isEmbedded && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/usuarios")}> 
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Avatar className="h-16 w-16">
              <AvatarImage src={getUserPhotoUrl(user) || undefined} alt={user.nome_completo} />
              <AvatarFallback className="text-lg">
                {getUserInitials(user.nome_completo)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-3xl font-bold">{user.nome_completo}</h1>
              <p className="text-muted-foreground">
                Usuário desde {format(new Date(user.created_at), "dd/MM/yyyy")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* KPIs Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Vendido</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? `R$ ${stats.totalVendido.toFixed(2)}` : "-"}
            </div>
            <p className="text-xs text-muted-foreground">Todas as vendas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Vendas</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalVendas || 0}</div>
            <p className="text-xs text-muted-foreground">Vendas confirmadas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ticket Médio</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? `R$ ${stats.ticketMedio.toFixed(2)}` : "-"}
            </div>
            <p className="text-xs text-muted-foreground">Por venda</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {user.comissao ? "Comissão Total" : "Status"}
            </CardTitle>
            {user.comissao ? (
              <Award className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Shield className="h-4 w-4 text-muted-foreground" />
            )}
          </CardHeader>
          <CardContent>
            {user.comissao ? (
              <>
                <div className="text-2xl font-bold">
                  {stats ? `R$ ${stats.totalComissao.toFixed(2)}` : "-"}
                </div>
                <p className="text-xs text-muted-foreground">{user.comissao}% de comissão</p>
              </>
            ) : (
              <Badge variant={getStatusBadge(user.status)} className="text-lg">
                {user.status}
              </Badge>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cards Adicionais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Vendas no Mês</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.vendasMes || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Vendas na Semana</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.vendasSemana || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Última Venda</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold">
              {stats?.ultimaVenda
                ? format(new Date(stats.ultimaVenda), "dd/MM/yyyy")
                : "Nunca"}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs Content */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="sales">Vendas</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="data">Dados Cadastrais</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="permissions">Permissões</TabsTrigger>
        </TabsList>

        {/* Visão Geral */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Informações Básicas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Informações de Contato
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {user.telefone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{user.telefone}</span>
                  </div>
                )}
                {user.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{user.email}</span>
                  </div>
                )}
                {user.data_nascimento && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{format(new Date(user.data_nascimento), "dd/MM/yyyy")}</span>
                  </div>
                )}
                {user.cargo && (
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    <span>{user.cargo}</span>
                  </div>
                )}
                <Separator />
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-muted-foreground" />
                  <Badge variant={getStatusBadge(user.status)}>{user.status}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>Perfil: {user.roles?.name || "Sem perfil"}</span>
                </div>
                {isAdminSaas && user.tenants && (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Tenant:</span>
                    <Badge variant="secondary">{user.tenants.name}</Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Produtos */}
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Produtos Vendidos</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.topProducts && stats.topProducts.length > 0 ? (
                  <div className="space-y-3">
                    {stats.topProducts.map((product, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{product.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {product.quantity}x unidades
                          </p>
                        </div>
                        <Badge variant="secondary">R$ {product.revenue.toFixed(2)}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    Nenhuma venda realizada
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Vendas Mensais */}
            <Card>
              <CardHeader>
                <CardTitle>Evolução de Vendas (Mensal)</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.monthlySales && stats.monthlySales.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.monthlySales}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">
                    Sem dados de vendas
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Métodos de Pagamento */}
            <Card>
              <CardHeader>
                <CardTitle>Métodos de Pagamento</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.paymentMethods && stats.paymentMethods.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPieChart>
                      <Pie
                        data={stats.paymentMethods}
                        dataKey="value"
                        nameKey="method"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label
                      >
                        {stats.paymentMethods.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">
                    Sem dados de pagamento
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Vendas */}
        <TabsContent value="sales" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Vendas</CardTitle>
            </CardHeader>
            <CardContent>
              {transactionsLoading ? (
                <p className="text-center py-8">Carregando...</p>
              ) : transactions && transactions.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Produtos</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {format(new Date(transaction.created_at), "dd/MM/yyyy HH:mm")}
                        </TableCell>
                        <TableCell>
                          {transaction.clients?.nome || "Cliente não identificado"}
                        </TableCell>
                        <TableCell>
                          {transaction.transaction_items?.length || 0} item(s)
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              transaction.status === "paga" || transaction.status === "confirmada"
                                ? "default"
                                : "outline"
                            }
                          >
                            {transaction.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          R$ {Number(transaction.valor_liquido || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  Nenhuma venda registrada
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics */}
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top Clientes */}
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Clientes</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.topClients && stats.topClients.length > 0 ? (
                  <div className="space-y-3">
                    {stats.topClients.map((client, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{client.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {client.quantidade} compra(s)
                          </p>
                        </div>
                        <Badge variant="secondary">R$ {client.revenue.toFixed(2)}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    Nenhum cliente registrado
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Vendas por Status */}
            <Card>
              <CardHeader>
                <CardTitle>Vendas por Status</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.salesByStatus && stats.salesByStatus.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.salesByStatus}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="status" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" fill="hsl(var(--primary))" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">
                    Sem dados disponíveis
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Gráficos Adicionais */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Vendas por Dia da Semana */}
            <Card>
              <CardHeader>
                <CardTitle>Vendas por Dia da Semana</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.salesByDayOfWeek && stats.salesByDayOfWeek.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.salesByDayOfWeek}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="hsl(var(--primary))" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">
                    Sem dados disponíveis
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Vendas por Hora */}
            <Card>
              <CardHeader>
                <CardTitle>Vendas por Hora do Dia</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.salesByHour && stats.salesByHour.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.salesByHour}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="hsl(var(--secondary))" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">
                    Sem dados disponíveis
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Dados Cadastrais */}
        <TabsContent value="data" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Dados Cadastrais</CardTitle>
              {!isEditing && (
                <Button variant="outline" onClick={() => setIsEditing(true)}>
                  Editar
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nome_completo">Nome Completo *</Label>
                      <Input
                        id="nome_completo"
                        value={formData.nome_completo}
                        onChange={(e) =>
                          setFormData({ ...formData, nome_completo: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <Input id="email" type="email" value={user.email} disabled />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="telefone">Telefone</Label>
                      <Input
                        id="telefone"
                        value={formData.telefone}
                        onChange={(e) => {
                          const formatted = formatPhoneBR(e.target.value);
                          setFormData({ ...formData, telefone: formatted });
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cargo">Cargo</Label>
                      <Input
                        id="cargo"
                        value={formData.cargo}
                        onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="data_nascimento">Data de Nascimento</Label>
                      <Input
                        id="data_nascimento"
                        type="date"
                        value={formData.data_nascimento}
                        onChange={(e) =>
                          setFormData({ ...formData, data_nascimento: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ativo">Ativo</SelectItem>
                          <SelectItem value="inativo">Inativo</SelectItem>
                          <SelectItem value="suspenso">Suspenso</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="role_id">Perfil de Acesso</Label>
                      <Select
                        value={formData.role_id}
                        onValueChange={(value) => setFormData({ ...formData, role_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o perfil" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableRoles.map((role) => (
                            <SelectItem key={role.id} value={role.id}>
                              {role.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={updateMutation.isPending}>
                      <Save className="mr-2 h-4 w-4" />
                      Salvar Alterações
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsEditing(false);
                        // Resetar formData para os valores originais do usuário
                        if (user) {
                          setFormData({
                            nome_completo: user.nome_completo,
                            telefone: user.telefone || "",
                            cargo: user.cargo || "",
                            status: user.status || "ativo",
                            data_nascimento: user.data_nascimento || "",
                            role_id: user.role_id || "",
                          });
                        }
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Nome Completo</p>
                      <p>{user.nome_completo}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Email</p>
                      <p>{user.email}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Telefone</p>
                      <p>{user.telefone || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Cargo</p>
                      <p>{user.cargo || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Data de Nascimento
                      </p>
                      <p>
                        {user.data_nascimento
                          ? format(new Date(user.data_nascimento), "dd/MM/yyyy")
                          : "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Status</p>
                      <Badge variant={getStatusBadge(user.status)}>{user.status}</Badge>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">
                        Perfil de Acesso
                      </p>
                      <p>{user.roles?.name || "-"}</p>
                    </div>
                    {user.comissao && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Comissão</p>
                        <p>{user.comissao}%</p>
                      </div>
                    )}
                    {isAdminSaas && (
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Tenant</p>
                        <p>{user.tenants?.name || "-"}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline */}
        <TabsContent value="timeline" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Timeline de Atividades</CardTitle>
            </CardHeader>
            <CardContent>
              {transactionsLoading ? (
                <p className="text-center py-8">Carregando...</p>
              ) : timelineActivities && timelineActivities.length > 0 ? (
                <div className="relative border-l-2 border-muted pl-6 space-y-6">
                  {timelineActivities.map((activity, index) => (
                    <div key={activity.id} className="relative">
                      <div className="absolute -left-[29px] w-4 h-4 rounded-full bg-primary border-4 border-background" />
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">
                          {format(
                            new Date(activity.created_at),
                            "dd/MM/yyyy 'às' HH:mm",
                            { locale: ptBR }
                          )}
                        </p>
                        <p className="font-medium">{activity.tipo}</p>
                        {activity.descricao && (
                          <p className="text-sm text-muted-foreground">{activity.descricao}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  Nenhuma atividade registrada
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Permissões */}
        <TabsContent value="permissions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Permissões e Acessos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">
                    Perfil de Acesso
                  </p>
                  <Badge variant="outline" className="text-base p-2">
                    {user.roles?.name || "Sem perfil"}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Status</p>
                  <Badge variant={getStatusBadge(user.status)} className="text-base p-2">
                    {user.status}
                  </Badge>
                </div>
                {isAdminSaas && user.tenants && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Tenant</p>
                    <Badge variant="secondary" className="text-base p-2">
                      {user.tenants.name}
                    </Badge>
                  </div>
                )}
                {permissions && permissions.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                      Permissões ({permissions.length})
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {permissions.map((permission: any, index: number) => (
                        <Badge key={index} variant="outline" className="justify-start">
                          {permission.module}: {permission.action}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
